import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnChanges,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  LOCALE_ID,
  Inject,
  OnInit,
  OnDestroy
} from '@angular/core';
import { getDayView, getDayViewHourGrid, CalendarEvent, DayView, DayViewHour } from 'calendar-utils';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';

const SEGMENT_HEIGHT: number = 30;


export class CalendarDayViewComponent implements OnChanges, OnInit, OnDestroy {

  /**
   * The current view date
   */
   viewDate: Date;

  /**
   * An array of events to display on view
   */
   events: CalendarEvent[] = [];

  /**
   * The number of segments in an hour. Must be <= 6
   */
   hourSegments: number = 2;

  /**
   * The day start hours in 24 hour time. Must be 0-23
   */
   dayStartHour: number = 0;

  /**
   * The day start minutes. Must be 0-59
   */
   dayStartMinute: number = 0;

  /**
   * The day end hours in 24 hour time. Must be 0-23
   */
   dayEndHour: number = 23;

  /**
   * The day end minutes. Must be 0-59
   */
   dayEndMinute: number = 59;

  /**
   * The width in pixels of each event on the view
   */
   eventWidth: number = 150;

  /**
   * An observable that when emitted on will re-render the current view
   */
   refresh: Subject<any>;

  /**
   * The locale used to format dates
   */
   locale: string;

  /**
   * A function that will be called before each hour segment is called. The first argument will contain the hour segment.
   * If you add the `cssClass` property to the segment it will add that class to the hour segment in the template
   */
   hourSegmentModifier: Function;

  /**
   * Called when an event title is clicked
   */
   eventClicked: EventEmitter<{event: CalendarEvent}> = new EventEmitter<{event: CalendarEvent}>();

  /**
   * Called when an hour segment is clicked
   */
   hourSegmentClicked: EventEmitter<{date: Date}> = new EventEmitter<{date: Date}>();

  hours: DayViewHour[] = [];
  view: DayView;
  width: number = 0;
  refreshSubscription: Subscription;

  constructor(private cdr: ChangeDetectorRef,  locale: string) {
    this.locale = locale;
  }

  ngOnInit(): void {
    if (this.refresh) {
      this.refreshSubscription = this.refresh.subscribe(() => {
        this.refreshAll();
        this.cdr.markForCheck();
      });
    }
  }

  ngOnDestroy(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: any): void {

    if (
      changes.viewDate ||
      changes.dayStartHour ||
      changes.dayStartMinute ||
      changes.dayEndHour ||
      changes.dayEndMinute
    ) {
      this.refreshHourGrid();
    }

    if (
      changes.viewDate ||
      changes.events ||
      changes.dayStartHour ||
      changes.dayStartMinute ||
      changes.dayEndHour ||
      changes.dayEndMinute ||
      changes.eventWidth
    ) {
      this.refreshView();
    }

  }

  private refreshHourGrid(): void {
    this.hours = getDayViewHourGrid({
      viewDate: this.viewDate,
      hourSegments: this.hourSegments,
      dayStart: {
        hour: this.dayStartHour,
        minute: this.dayStartMinute
      },
      dayEnd: {
        hour: this.dayEndHour,
        minute: this.dayEndMinute
      }
    });
    if (this.hourSegmentModifier) {
      this.hours.forEach(hour => {
        hour.segments.forEach(segment => this.hourSegmentModifier(segment));
      });
    }
  }

  private refreshView(): void {
    this.view = getDayView({
      events: this.events,
      viewDate: this.viewDate,
      hourSegments: this.hourSegments,
      dayStart: {
        hour: this.dayStartHour,
        minute: this.dayStartMinute
      },
      dayEnd: {
        hour: this.dayEndHour,
        minute: this.dayEndMinute
      },
      eventWidth: this.eventWidth,
      segmentHeight: SEGMENT_HEIGHT
    });
  }

  private refreshAll(): void {
    this.refreshHourGrid();
    this.refreshView();
  }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'mwl-calendar-day-view',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="cal-day-view">
      <mwl-calendar-all-day-event
        *ngFor="let event of view.allDayEvents"
        [event]="event"
        (eventClicked)="eventClicked.emit({event: event})">
      </mwl-calendar-all-day-event>
      <div class="cal-hour-rows">
        <div class="cal-events">
          <mwl-calendar-day-view-event
            *ngFor="let dayEvent of view?.events"
            [dayEvent]="dayEvent"
            (eventClicked)="eventClicked.emit({event: dayEvent.event})">
          </mwl-calendar-day-view-event>
        </div>
        <div class="cal-hour" *ngFor="let hour of hours" [style.minWidth.px]="view?.width + 70">
          <mwl-calendar-day-view-hour-segment
            *ngFor="let segment of hour.segments"
            [segment]="segment"
            [locale]="locale"
            (click)="hourSegmentClicked.emit({date: segment.date})">
          </mwl-calendar-day-view-hour-segment>
        </div>
      </div>
    </div>
  `
}, ] },
];
/** @nocollapse */
static ctorParameters: ({type: any, decorators?: DecoratorInvocation[]}|null)[] = [
{type: ChangeDetectorRef, },
{type: undefined, decorators: [{ type: Inject, args: [LOCALE_ID, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'viewDate': [{ type: Input },],
'events': [{ type: Input },],
'hourSegments': [{ type: Input },],
'dayStartHour': [{ type: Input },],
'dayStartMinute': [{ type: Input },],
'dayEndHour': [{ type: Input },],
'dayEndMinute': [{ type: Input },],
'eventWidth': [{ type: Input },],
'refresh': [{ type: Input },],
'locale': [{ type: Input },],
'hourSegmentModifier': [{ type: Input },],
'eventClicked': [{ type: Output },],
'hourSegmentClicked': [{ type: Output },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
